import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom'; // SIPAK_RECOVERY_QA20_V1_LINK
import {
  ArrowLeft, Upload, FileText, CheckCircle2, AlertTriangle, XCircle,
  Settings, Users, FolderOpen, Search, Download, Plus, RefreshCw,
  ChevronDown, ChevronUp, Save, Send, LockKeyhole, Database,
  FileCheck2, CalendarDays, Eye, Trash2, Copy, ShieldCheck,
  ArrowRight, UserCheck, ClipboardList, CircleHelp, RotateCcw
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api-pak.sudindikjt2.site';
const MAX_FILE_BYTES = 900 * 1024;

const withKpTestUser = (url) => {
  const testUserId = sessionStorage.getItem('kpTestUserId');
  if (!testUserId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}test_user_id=${encodeURIComponent(testUserId)}`;
};

const kpFetch = (url, options = {}) => window.fetch(withKpTestUser(url), options);

const authHeaders = (json = true) => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  ...(json ? { 'Content-Type': 'application/json' } : {})
});

const fmtDate = value => {
  if (!value) return '-';
  const d = new Date(String(value).slice(0, 10) + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
};

const fmtSize = bytes => {
  if (!bytes) return '-';
  return `${(Number(bytes) / 1024).toFixed(0)} KB`;
};

const effectiveSubmissionData = row => {
  const proposed = row?.submission_snapshot;
  if (proposed && typeof proposed === 'object' && Object.keys(proposed).length) return proposed;
  return row?.simpeg_snapshot || {};
};

const pakTypeLabel = value => ({
  KONVENSIONAL: 'PAK Konvensional',
  INTEGRASI: 'PAK Integrasi',
  KONVERSI: 'PAK Konversi'
}[String(value || '').toUpperCase()] || String(value || 'PAK'));

const pakDateValue = value => {
  const raw = String(value || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : '';
};

const sortPakRows = (rows = []) => [...rows].sort((a, b) => {
  const aStart = pakDateValue(a?.period_start) || '9999-12-31';
  const bStart = pakDateValue(b?.period_start) || '9999-12-31';
  if (aStart !== bStart) return aStart.localeCompare(bStart);
  const aEnd = pakDateValue(a?.period_end) || '9999-12-31';
  const bEnd = pakDateValue(b?.period_end) || '9999-12-31';
  if (aEnd !== bEnd) return aEnd.localeCompare(bEnd);
  return String(a?.pak_type || '').localeCompare(String(b?.pak_type || ''));
});

const analyzePakRows = (rows = []) => {
  const filled = (rows || []).filter(r => r && (
    r.pak_type || r.period_start || r.period_end || String(r.ak_akhir ?? '').trim() !== ''
  ));
  const sorted = sortPakRows(filled);
  const warnings = [];

  filled.forEach((r, i) => {
    if (!r.pak_type) warnings.push(`PAK ${i + 1}: jenis PAK belum dipilih.`);
    if (!pakDateValue(r.period_start) || !pakDateValue(r.period_end)) {
      warnings.push(`PAK ${i + 1}: periode belum lengkap.`);
    }
  });

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const ps = pakDateValue(prev.period_start);
    const pe = pakDateValue(prev.period_end);
    const cs = pakDateValue(curr.period_start);
    const ce = pakDateValue(curr.period_end);

    if (ps && pe && cs && ce && ps === cs && pe === ce) {
      warnings.push(`Ada dua PAK dengan periode yang sama (${fmtDate(cs)} s.d. ${fmtDate(ce)}).`);
    } else if (pe && cs && cs <= pe) {
      warnings.push(`Periode ${pakTypeLabel(curr.pak_type)} bertumpang tindih dengan PAK sebelumnya. Mohon cek dokumen.`);
    }

    const prevAk = Number(String(prev.ak_akhir ?? '').replace(',', '.'));
    const currAk = Number(String(curr.ak_akhir ?? '').replace(',', '.'));
    if (Number.isFinite(prevAk) && Number.isFinite(currAk) && currAk + 0.001 < prevAk) {
      warnings.push(`AK akhir pada ${pakTypeLabel(curr.pak_type)} lebih kecil dari PAK sebelumnya. Mohon cek angka pada dokumen.`);
    }
  }

  const isSorted = filled.length === sorted.length && filled.every((r, i) => r === sorted[i]);
  return { count: filled.length, sorted, warnings, isSorted };
};

const attentionReasonsForRow = row => {
  if (!row || ['DRAFT', 'APPROVED', 'DEFERRED'].includes(row.status)) return [];
  const reasons = [];
  if (Number(row.bts_document_count || 0) > 0) reasons.push(`${row.bts_document_count} dokumen perlu diperbaiki`);
  if (Number(row.pak_count || 0) === 0) reasons.push('Riwayat PAK belum ada');
  if (Number(row.pak_gabungan_count || 0) === 0) reasons.push('PAK Gabungan belum ada');
  if (!row.ak_override && String(row.ak_status || '').toLowerCase() !== 'cukup') reasons.push('Angka Kredit perlu dicek');
  if (row.data_match === false) reasons.push('Ada data usulan yang berbeda');
  return reasons;
};

const candidateMatchesRequirement = (code, name) => {
  const requestedYear=String(code||'').match(/^SKP_(20\d{2})$/);
  if(requestedYear) return new RegExp(String.raw`\bskp(?:\s+tahun)?\s*${requestedYear[1]}\b`,'i').test(String(name||'').toLowerCase().replace(/[_-]+/g,' '));
  const n = String(name || '').toLowerCase().replace(/[_-]+/g, ' ');

  const rules = {
    IJAZAH_TERAKHIR: ['ijazah'],
    TRANSKRIP_TERAKHIR: ['transkrip'],
    PAK_GABUNGAN: ['pak', 'angka kredit'],
    SKP_2023: ['skp 2023', 'skp2023'],
    SKP_2024: ['skp 2024', 'skp2024'],
    SKP_2025: ['skp 2025', 'skp2025'],
    SPP: ['spp'],
    SK_JAFUNG: ['jafung', 'jabatan fungsional', 'jabfung'],
    SK_PANGKAT_TERAKHIR: ['sk pangkat', 'pangkat terakhir'],
    SK_CPNS: ['cpns'],
    SK_PNS: ['sk pns', ' pns'],
    SERTIFIKAT_UKOM: ['ukom', 'uji kompetensi']
  };

  const keywords = rules[code] || [];
  return keywords.length ? keywords.some(k => n.includes(k)) : n.replace(/[^a-z0-9]/g,'') === String(code||'').toLowerCase().replace(/[^a-z0-9]/g,'');
};

// SIPAK_KP_LAUNCH_FIX_V3: Jangan tawarkan jenis/tahun yang tidak sesuai.
const candidatesForRequirement = (code, all = []) =>
  all.filter(item => candidateMatchesRequirement(code,item.name));

function Banner({ type = 'info', children }) {
  const cls = {
    info: 'border-blue-200 bg-blue-50 text-blue-800',
    warn: 'border-amber-200 bg-amber-50 text-amber-800',
    error: 'border-rose-200 bg-rose-50 text-rose-800',
    ok: 'border-emerald-200 bg-emerald-50 text-emerald-800'
  }[type];
  return <div className={`border rounded-xl px-4 py-3 text-sm ${cls}`}>{children}</div>;
}

function Pill({ children, tone = 'slate' }) {
  const cls = {
    slate: 'bg-slate-100 text-slate-700',
    green: 'bg-emerald-100 text-emerald-700',
    yellow: 'bg-amber-100 text-amber-700',
    red: 'bg-rose-100 text-rose-700',
    blue: 'bg-blue-100 text-blue-700'
  }[tone];
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>{children}</span>;
}

export default function KpEntryPage() {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    kpFetch(`${API}/api/kp/access`, { headers: authHeaders(false) })
      .then(async r => ({ ok: r.ok, data: await r.json() }))
      .then(({ ok, data }) => {
        if (!ok && !data.locked) throw new Error(data.msg || 'Gagal membaca akses.');
        setAccess(data);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading label="Membuka modul Kenaikan Pangkat..." />;
  if (error) return <CenteredError message={error} />;

  if (!access?.allowed) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="max-w-xl w-full bg-white border rounded-2xl p-7 shadow-sm">
          <LockKeyhole size={36} className="text-slate-700 mb-4" />
          <h1 className="text-2xl font-bold">Usulan Kenaikan Pangkat</h1>
          <p className="text-slate-600 mt-2">Modul masih dikunci untuk uji coba Admin.</p>
          <Link to="/dashboard" className="inline-flex mt-5 px-4 py-2 rounded-xl bg-slate-900 text-white font-bold no-underline">Kembali ke Dashboard</Link>
        </div>
      </div>
    );
  }

  const testUserId = sessionStorage.getItem('kpTestUserId');

  if (access.role === 'admin' && testUserId) {
    return <TeacherPage testMode />;
  }

  if (access.role === 'admin') return <AdminPage />;
  if (access.role === 'satlak') return <ReviewerPage />;
  return <TeacherPage />;
}

function PageShell({ title, subtitle, children, right }) {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-[1550px] mx-auto px-4 md:px-8 py-7">
        <div className="flex flex-col md:flex-row gap-4 md:items-start md:justify-between mb-6">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 no-underline mb-3"><ArrowLeft size={16}/> Dashboard</Link>
            <h1 className="text-2xl md:text-3xl font-bold">{title}</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-3xl">{subtitle}</p>
          </div>
          {right}
        </div>
        {children}
      </div>
    </div>
  );
}

function Loading({ label }) {
  return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-600">{label}</div></div>;
}

function CenteredError({ message }) {
  return <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"><div className="bg-white border border-rose-200 rounded-xl p-6 text-rose-700 max-w-xl">{message}</div></div>;
}

function AdminPage() {
  const [tab, setTab] = useState('list');
  return (
    <PageShell
      title="Usulan Kenaikan Pangkat"
      subtitle="Mode uji coba Admin. Pilih Simulasi Guru untuk mencoba alur yang sama persis dengan akun guru. Data otomatis diambil dari SIPAK, tetapi Guru dapat melengkapi atau memperbaiki data khusus untuk usulan ini."
      right={<Pill tone="yellow">MODE UJI COBA ADMIN</Pill>}
    >
      <div className="flex flex-wrap gap-2 mb-5">
        {[
          ['list','Daftar Usulan',Users],
          ['test','Simulasi Guru',Eye],
          ['period','Periode & Akses',CalendarDays],
          ['simpeg','Data Acuan',Database]
        ].map(([key,label,Icon]) => (
          <button key={key} onClick={() => setTab(key)} className={`px-4 py-2 rounded-xl font-bold text-sm inline-flex items-center gap-2 border ${tab===key?'bg-slate-900 text-white border-slate-900':'bg-white text-slate-700 border-slate-200'}`}><Icon size={16}/>{label}</button>
        ))}
      </div>
      {tab === 'list' && <SubmissionList admin />}
      {tab === 'test' && <TeacherSimulationPicker />}
      {tab === 'period' && <PeriodSettings />}
      {tab === 'simpeg' && <SimpegImport />}
    </PageShell>
  );
}

function ReviewerPage() {
  return (
    <PageShell title="Verifikasi Usulan Kenaikan Pangkat" subtitle="Daftar otomatis dibatasi sesuai kecamatan akun Satlak. Gunakan pencarian dan filter untuk memeriksa kelengkapan, hasil cross-check, dan status BTS.">
      <SubmissionList />
    </PageShell>
  );
}

function TeacherSimulationPicker() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const search = async () => {
    setLoading(true);

    try {
      const r = await kpFetch(
        `${API}/api/kp/admin/test-teachers?q=${encodeURIComponent(q)}`,
        { headers: authHeaders(false) }
      );

      const d = await r.json();

      if (!r.ok) {
        alert(d.msg || 'Gagal memuat guru SIPAK.');
        setRows([]);
        return;
      }

      setRows(Array.isArray(d) ? d : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    search();
  }, []);

  const syncAll = async () => {
    if (!window.confirm(
      'Salin data profil seluruh akun Guru SIPAK menjadi data acuan uji coba modul Kenaikan Pangkat?\n\nIni tidak mengubah profil SIPAK asli.'
    )) return;

    setSyncing(true);

    try {
      const r = await kpFetch(
        `${API}/api/kp/admin/sync-sipak-master`,
        {
          method: 'POST',
          headers: authHeaders()
        }
      );

      const d = await r.json();

      if (!r.ok) {
        return alert(d.msg || 'Gagal sinkron data SIPAK.');
      }

      alert(`${d.synced || 0} data guru berhasil disinkronkan.`);
      await search();
    } finally {
      setSyncing(false);
    }
  };

  const enterTeacher = row => {
    if (!row?.user_id) return;

    sessionStorage.setItem(
      'kpTestUserId',
      String(row.user_id)
    );

    sessionStorage.setItem(
      'kpTestTeacherName',
      row.full_name || row.nip || 'Guru'
    );

    window.location.reload();
  };

  return (
    <div className="space-y-5">
      <Banner type="info">
        <b>Simulasi POV Bapak/Ibu Guru.</b>{' '}
        Pilih akun Guru yang sudah ada di SIPAK. Admin akan melihat form
        pengusulan persis dari sudut pandang guru tanpa mengetahui atau
        menggunakan password guru.
      </Banner>

      <div className="bg-white border rounded-2xl p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-lg">
              Pilih Guru SIPAK
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Data nama, NIP, golongan, jabatan, unit kerja, dokumen lama,
              dan status PAK diambil dari data SIPAK yang sudah ada.
            </p>
          </div>

          <button
            type="button"
            disabled={syncing}
            onClick={syncAll}
            className="px-4 py-2.5 rounded-xl bg-blue-600 disabled:bg-slate-300 text-white font-bold inline-flex gap-2 items-center justify-center"
          >
            <RefreshCw
              size={16}
              className={syncing ? 'animate-spin' : ''}
            />
            {syncing
              ? 'Sinkron...'
              : 'Sinkron Semua Guru SIPAK'}
          </button>
        </div>

        <div className="mt-4 flex gap-2">
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') search();
              }}
              placeholder="Cari nama, NIP, unit kerja, kecamatan, golongan..."
              className="w-full border rounded-xl pl-10 pr-3 py-2.5"
            />
          </div>

          <button
            type="button"
            onClick={search}
            className="px-4 py-2.5 rounded-xl bg-slate-900 text-white font-bold"
          >
            Cari
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b">
          <div className="font-bold">
            Data Guru SIPAK
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Maksimal 500 hasil per pencarian.
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500">
            Memuat data guru...
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Guru tidak ditemukan.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1050px]">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3">Nama / NIP</th>
                  <th className="text-left p-3">Golongan</th>
                  <th className="text-left p-3">Jabatan</th>
                  <th className="text-left p-3">Unit Kerja</th>
                  <th className="text-left p-3">Kecamatan</th>
                  <th className="text-left p-3">AK</th>
                  <th className="text-center p-3">Dokumen SIPAK</th>
                  <th className="text-center p-3">Aksi</th>
                </tr>
              </thead>

              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.user_id}
                    className="border-t hover:bg-slate-50"
                  >
                    <td className="p-3">
                      <div className="font-bold">
                        {row.full_name || '-'}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {row.nip || '-'}
                      </div>
                    </td>

                    <td className="p-3">
                      {row.golongan || '-'}
                    </td>

                    <td className="p-3">
                      {row.jabatan || '-'}
                    </td>

                    <td className="p-3">
                      {row.unit_kerja || '-'}
                    </td>

                    <td className="p-3">
                      {row.kecamatan || '-'}
                    </td>

                    <td className="p-3">
                      <Pill
                        tone={
                          String(row.status_ak || '')
                            .toLowerCase() === 'cukup'
                            ? 'green'
                            : 'yellow'
                        }
                      >
                        {row.status_ak || 'Belum Dicek'}
                      </Pill>
                    </td>

                    <td className="p-3 text-center">
                      {row.existing_document_count || 0}
                    </td>

                    <td className="p-3 text-center">
                      <button
                        type="button"
                        onClick={() => enterTeacher(row)}
                        className="px-3 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs inline-flex gap-2 items-center"
                      >
                        <Eye size={14}/>
                        Lihat POV Guru
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PeriodSettings() {
  const [settings, setSettings] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    const [a,b] = await Promise.all([
      kpFetch(`${API}/api/kp/settings`, { headers: authHeaders(false) }).then(r=>r.json()),
      kpFetch(`${API}/api/kp/periods`, { headers: authHeaders(false) }).then(r=>r.json())
    ]);
    setSettings(a); setPeriods(Array.isArray(b)?b:[]);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    const r = await kpFetch(`${API}/api/kp/settings`, { method:'PUT', headers:authHeaders(), body:JSON.stringify(settings) });
    const d = await r.json();
    setSaving(false);
    if (!r.ok) return setMsg(d.msg || 'Gagal menyimpan.');
    setSettings(d); setMsg('Pengaturan akses tersimpan.');
  };

  const nextPeriod = async () => {
    const r = await kpFetch(`${API}/api/kp/periods/next`, { method:'POST', headers:authHeaders(), body:'{}' });
    const d = await r.json();
    if (!r.ok) return alert(d.msg || 'Gagal membuat periode.');
    await load();
  };

  if (!settings) return <Loading label="Memuat pengaturan..."/>;

  return <div className="grid xl:grid-cols-[420px_1fr] gap-5">
    <div className="bg-white border rounded-2xl p-5 h-fit">
      <h2 className="font-bold text-lg">Akses Modul</h2>
      <p className="text-xs text-slate-500 mt-1">Saat ini aman untuk pengujian karena mode Admin Saja aktif.</p>
      <Toggle label="Mode Uji Coba Admin Saja" checked={settings.admin_only} onChange={v=>setSettings({...settings,admin_only:v})}/>
      <Toggle label="Izinkan Satlak" checked={settings.satlak_enabled} onChange={v=>setSettings({...settings,satlak_enabled:v})}/>
      <Toggle label="Buka untuk Guru" checked={settings.teacher_enabled} onChange={v=>setSettings({...settings,teacher_enabled:v})}/>
      {settings.admin_only && <Banner type="warn">Selama <b>Mode Uji Coba Admin Saja</b> aktif, Satlak dan Guru tetap tidak dapat masuk walaupun toggle mereka dinyalakan.</Banner>}
      <button onClick={save} disabled={saving} className="mt-4 w-full px-4 py-2.5 rounded-xl bg-blue-600 text-white font-bold inline-flex items-center justify-center gap-2"><Save size={16}/>{saving?'Menyimpan...':'Simpan Akses'}</button>
      {msg && <div className="text-xs text-emerald-700 mt-2">{msg}</div>}
    </div>

    <div className="bg-white border rounded-2xl overflow-hidden">
      <div className="p-5 border-b flex items-center justify-between gap-3"><div><h2 className="font-bold text-lg">Periode TMT</h2><p className="text-xs text-slate-500 mt-1">Periode berikut otomatis maju satu bulan TMT dan jadwal pengusulan mundur dua bulan.</p></div><button onClick={nextPeriod} className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm inline-flex gap-2 items-center"><Plus size={16}/>Buat Periode Berikut</button></div>
      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[760px]"><thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr><th className="text-left p-4">TMT</th><th className="text-left p-4">Pengusulan</th><th className="text-left p-4">Status</th><th className="text-left p-4">Folder Drive</th></tr></thead><tbody>{periods.map(p=><tr key={p.id} className="border-t"><td className="p-4 font-bold">{fmtDate(p.tmt_date)}</td><td className="p-4">{fmtDate(p.submission_open_at)} – {fmtDate(p.submission_close_at)}</td><td className="p-4"><Pill tone="green">{p.status}</Pill></td><td className="p-4 text-xs">{p.drive_folder_id || 'Dibuat otomatis saat digunakan'}</td></tr>)}</tbody></table></div>
    </div>
  </div>;
}

function Toggle({ label, checked, onChange }) {
  return <label className="flex items-center justify-between gap-3 py-4 border-b cursor-pointer"><span className="font-semibold text-sm">{label}</span><input type="checkbox" checked={Boolean(checked)} onChange={e=>onChange(e.target.checked)} className="w-5 h-5"/></label>;
}

function SimpegImport() {
  const [file,setFile] = useState(null);
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [q,setQ] = useState('');
  const [rows,setRows] = useState([]);

  const search = async () => {
    const r = await kpFetch(`${API}/api/kp/simpeg?q=${encodeURIComponent(q)}`, { headers:authHeaders(false) });
    const d = await r.json(); setRows(Array.isArray(d)?d:[]);
  };
  useEffect(()=>{search();},[]);

  const upload = async () => {
    if(!file) return;
    setLoading(true); setResult(null);
    const fd = new FormData(); fd.append('file',file);
    const r = await kpFetch(`${API}/api/kp/simpeg/import`, { method:'POST', headers:authHeaders(false), body:fd });
    const d = await r.json(); setLoading(false); setResult(d); if(r.ok) search();
  };

  return <div className="space-y-5">
    <div className="bg-white border rounded-2xl p-5">
      <h2 className="font-bold text-lg">Data Acuan Pengusulan</h2>
      <p className="text-sm text-slate-600 mt-2">
        Untuk uji coba, data dapat disalin dari profil Guru SIPAK melalui menu
        <b> Simulasi Guru</b>. Import Excel tetap tersedia untuk tahap berikutnya
        ketika data resmi SIMPEG akan digunakan sebagai pembanding.
      </p>
      <div className="mt-4 flex flex-col md:flex-row gap-3"><input type="file" accept=".xlsx,.xls" onChange={e=>setFile(e.target.files?.[0]||null)} className="flex-1 border rounded-xl p-2.5"/><button disabled={!file||loading} onClick={upload} className="px-5 py-2.5 rounded-xl bg-blue-600 disabled:bg-slate-300 text-white font-bold inline-flex items-center gap-2"><Upload size={16}/>{loading?'Mengimpor...':'Import'}</button></div>
      {result && <div className="mt-3"><Banner type={result.imported>=0?'ok':'error'}>Berhasil: {result.imported||0} · Gagal: {result.skipped||0}{result.msg?` · ${result.msg}`:''}</Banner></div>}
    </div>
    <div className="bg-white border rounded-2xl overflow-hidden">
      <div className="p-4 border-b flex gap-2"><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cari nama / NIP / unit kerja" className="flex-1 border rounded-xl px-3"/><button onClick={search} className="px-4 py-2 rounded-xl bg-slate-100 font-bold"><Search size={16}/></button></div>
      <div className="overflow-x-auto"><table className="w-full text-sm min-w-[900px]"><thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr><th className="p-3 text-left">Nama</th><th className="p-3 text-left">NIP/NRK</th><th className="p-3 text-left">Gol/TMT</th><th className="p-3 text-left">Jabatan</th><th className="p-3 text-left">Unit Kerja</th><th className="p-3 text-left">Kecamatan</th></tr></thead><tbody>{rows.map(r=><tr key={r.nip} className="border-t"><td className="p-3 font-semibold">{r.full_name}</td><td className="p-3">{r.nip}<div className="text-xs text-slate-400">NRK {r.nrk||'-'}</div></td><td className="p-3">{r.golongan||'-'}<div className="text-xs text-slate-400">{fmtDate(r.tmt_golongan)}</div></td><td className="p-3">{r.jabatan||'-'}</td><td className="p-3">{r.unit_kerja||'-'}</td><td className="p-3">{r.kecamatan||'-'}</td></tr>)}</tbody></table></div>
    </div>
  </div>;
}

function SubmissionList({ admin=false }) {
  const [periods,setPeriods] = useState([]);
  const [periodId,setPeriodId] = useState('');
  const [rows,setRows] = useState([]);
  const [q,setQ] = useState('');
  const [status,setStatus] = useState('ALL');
  const [attentionOnly,setAttentionOnly] = useState(false);
  const [detail,setDetail] = useState(null);
  const [loading,setLoading] = useState(true);

  const loadPeriods = async () => {
    const r=await kpFetch(`${API}/api/kp/periods`,{headers:authHeaders(false)}); const d=await r.json();
    const arr=Array.isArray(d)?d:[]; setPeriods(arr); if(!periodId&&arr.length) setPeriodId(String(arr[0].id));
  };
  useEffect(()=>{loadPeriods();},[]);
  useEffect(()=>{ if(periodId) loadRows(); },[periodId]);

  const loadRows = async () => {
    setLoading(true);
    const r=await kpFetch(`${API}/api/kp/submissions?period_id=${periodId}`,{headers:authHeaders(false)}); const d=await r.json();
    setRows(Array.isArray(d)?d:[]); setLoading(false);
  };

  const filtered=useMemo(()=>rows.filter(r=>{
    const s=effectiveSubmissionData(r);
    const text=`${r.nip} ${s.full_name||''} ${s.nrk||''} ${s.unit_kerja||''} ${s.kecamatan||''}`.toLowerCase();
    const matchText=!q||text.includes(q.toLowerCase());
    const matchStatus=status==='ALL'||r.status===status;
    const matchAttention=!attentionOnly||attentionReasonsForRow(r).length>0;
    return matchText&&matchStatus&&matchAttention;
  }),[rows,q,status,attentionOnly]);

  const period=periods.find(p=>String(p.id)===String(periodId));
  const attentionTotal=rows.filter(r=>attentionReasonsForRow(r).length>0).length;

  return <div className="space-y-4">
    <div className="bg-white border rounded-2xl p-4 flex flex-col xl:flex-row gap-3 xl:items-center">
      <select value={periodId} onChange={e=>setPeriodId(e.target.value)} className="border rounded-xl h-11 px-3 min-w-[230px]">{periods.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select>
      <div className="relative flex-1"><Search size={17} className="absolute left-3 top-3 text-slate-400"/><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cari nama, NIP, unit kerja, kecamatan..." className="w-full h-11 pl-10 pr-3 border rounded-xl"/></div>
      <select value={status} onChange={e=>setStatus(e.target.value)} className="border rounded-xl h-11 px-3"><option value="ALL">Semua status</option>{['DRAFT','SUBMITTED','BTS','SATLAK_APPROVED','APPROVED','DEFERRED'].map(x=><option key={x} value={x}>{({DRAFT:'Belum Dikirim',SUBMITTED:'Menunggu Satlak',BTS:'Perlu Perbaikan',SATLAK_APPROVED:'Menunggu Sudin',APPROVED:'Terverifikasi Sudin',DEFERRED:'Dialihkan TMT'})[x]}</option>)}</select>
      <button type="button" onClick={()=>setAttentionOnly(v=>!v)} className={`h-11 px-4 rounded-xl font-bold inline-flex items-center gap-2 justify-center ${attentionOnly?'bg-amber-500 text-white':'bg-amber-50 text-amber-800 border border-amber-200'}`}><AlertTriangle size={16}/>Perlu Perhatian ({attentionTotal})</button>
      <button onClick={loadRows} className="h-11 px-4 rounded-xl bg-slate-100 font-bold"><RefreshCw size={16}/></button>
      {period && <a href={`${API}/api/kp/export.xlsx?period_id=${period.id}`} onClick={e=>{e.preventDefault(); downloadWithToken(`${API}/api/kp/export.xlsx?period_id=${period.id}`)}} className="h-11 px-4 rounded-xl bg-emerald-600 text-white font-bold inline-flex items-center gap-2 justify-center no-underline"><Download size={16}/>Unduh Excel</a>}
    </div>

    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">{[['TOTAL','Total'],['SUBMITTED','Menunggu Satlak'],['BTS','Perlu Perbaikan'],['SATLAK_APPROVED','Menunggu Sudin'],['APPROVED','Selesai'],['DEFERRED','Dialihkan TMT']].map(([x,label])=>{const n=x==='TOTAL'?rows.length:rows.filter(r=>r.status===x).length; return <div key={x} className="bg-white border rounded-xl p-4"><div className="text-2xl font-bold">{n}</div><div className="text-xs text-slate-500 mt-1">{label}</div></div>})}</div>

    <div className="bg-white border rounded-2xl overflow-hidden">
      {loading?<div className="p-8 text-center text-slate-500">Memuat...</div>:<div className="overflow-x-auto"><table className="w-full text-sm min-w-[1320px]"><thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr><th className="p-3 text-left">Nama / NIP</th><th className="p-3 text-left">Gol</th><th className="p-3 text-left">Unit Kerja</th><th className="p-3 text-left">Kecamatan</th><th className="p-3 text-left">Dokumen</th><th className="p-3 text-left">AK</th><th className="p-3 text-left">Data</th><th className="p-3 text-left">Pemeriksaan</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr></thead><tbody>{filtered.map(r=>{const s=effectiveSubmissionData(r);const reasons=attentionReasonsForRow(r);return <tr key={r.id} className={`border-t ${reasons.length?'bg-amber-50/30':''}`}><td className="p-3"><div className="font-bold">{s.full_name||'-'}</div><div className="text-xs text-slate-400">{r.nip}</div></td><td className="p-3">{s.golongan||'-'}</td><td className="p-3 max-w-[260px] whitespace-normal">{s.unit_kerja||'-'}</td><td className="p-3">{s.kecamatan||'-'}</td><td className="p-3">{r.document_count||0} file{Number(r.bts_document_count||0)>0&&<div className="text-xs text-rose-600 mt-1">{r.bts_document_count} perlu diperbaiki</div>}</td><td className="p-3"><Pill tone={(r.ak_override||String(r.ak_status).toLowerCase()==='cukup')?'green':'yellow'}>{r.ak_override?'Disetujui petugas':String(r.ak_status).toLowerCase()==='cukup'?'Cukup':'Perlu dicek'}</Pill></td><td className="p-3">{r.data_match?<Pill tone="green">Sesuai</Pill>:<Pill tone="yellow">Perlu dicek</Pill>}</td><td className="p-3">{reasons.length?<div><Pill tone="yellow">Perlu perhatian · {reasons.length}</Pill><div className="mt-1 text-[11px] text-amber-800 max-w-[230px]">{reasons.slice(0,2).join(' · ')}</div></div>:<Pill tone="green">Aman</Pill>}</td><td className="p-3"><StatusPill status={r.status}/></td><td className="p-3"><button onClick={()=>openDetail(r.id,setDetail)} className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 font-bold inline-flex items-center gap-1"><Eye size={14}/>Periksa</button></td></tr>})}{!filtered.length&&<tr><td colSpan="10" className="p-10 text-center text-slate-500">Tidak ada usulan yang sesuai filter.</td></tr>}</tbody></table></div>}
    </div>
    {detail && <SubmissionModal data={detail} onClose={()=>setDetail(null)} onChanged={()=>{setDetail(null);loadRows();}} admin={admin}/>}  
  </div>;
}

function StatusPill({status}) {
  const meta = {
    DRAFT: ['Belum Dikirim', 'slate'],
    SUBMITTED: ['Menunggu Verifikasi Satlak', 'blue'],
    BTS: ['Perlu Perbaikan', 'red'],
    SATLAK_APPROVED: ['Lolos Satlak · Menunggu Sudin', 'yellow'],
    APPROVED: ['Terverifikasi Sudin', 'green'],
    DEFERRED: ['Dialihkan ke TMT Berikutnya', 'yellow']
  }[status] || [status || 'Draf', 'slate'];
  return <Pill tone={meta[1]}>{meta[0]}</Pill>;
}

async function openDetail(id,setDetail){const r=await kpFetch(`${API}/api/kp/submissions/${id}`,{headers:authHeaders(false)});const d=await r.json();if(!r.ok)return alert(d.msg||'Gagal');setDetail(d);}

function SubmissionModal({ data,onClose,onChanged,admin }) {
  const s=data.submission;
  const profile=data.submission_data||effectiveSubmissionData(s);
  const documents=data.documents||[];
  const requirements=data.requirements||[];
  const pakRows=data.pak_history||[];
  const pakAnalysis=analyzePakRows(pakRows);
  const missingRequired=requirements.filter(req=>req.required&&!documents.some(d=>d.document_code===req.code));
  const pendingDocReview=requirements.filter(req=>{
    if(!req.required)return false;
    const d=documents.find(x=>x.document_code===req.code);
    return !d||String(d.manual_status||'').toUpperCase()!=='SESUAI';
  });
  const dataChangeCount=(data.data_changes||[]).length;
  const [note,setNote]=useState(s.reviewer_note||'');
  const [docReviews,setDocReviews]=useState(()=>Object.fromEntries(documents.map(d=>[d.document_code,{status:d.manual_status||'BELUM_DICEK',note:d.reviewer_note||''}])));
  const [dataReview,setDataReview]=useState({status:'BELUM_DICEK',note:''});
  const [reviewBusy,setReviewBusy]=useState(false);
  const [reviewAction,setReviewAction]=useState('');


  const markAllDocumentsSuitable=()=>{
    const existingCodes=requirements
      .filter(req=>documents.some(d=>d.document_code===req.code))
      .map(req=>req.code);

    if(!existingCodes.length)return;

    const hasCorrection=existingCodes.some(code=>
      String(docReviews[code]?.status||'').toUpperCase()==='BTS'
    );

    if(hasCorrection&&!window.confirm(
      'Ada dokumen yang sebelumnya ditandai Perlu diperbaiki. Yakin ubah semua dokumen yang tersedia menjadi Sesuai?'
    ))return;

    setDocReviews(prev=>{
      const next={...prev};
      existingCodes.forEach(code=>{
        next[code]={...(next[code]||{}),status:'SESUAI',note:''};
      });
      return next;
    });
  };

  // KP_REVIEW_HTTP_SAFETY_V11: jangan parse halaman HTML 502 menjadi JSON.
  const review=async decision=>{
    if(reviewBusy)return;
    // KP_REVIEW_LOADING_V11_2: pesan proses mengikuti keputusan petugas.
    const action = decision==='BTS'
      ? 'Mengirim catatan perbaikan...'
      : decision==='SATLAK_APPROVED'
        ? 'Mensimulasikan pengiriman ke Sudin...'
        : !admin && s.status==='SUBMITTED'
          ? 'Mengirim usulan ke Sudin...'
          : 'Memvalidasi usulan di Sudin...';
    setReviewAction(action);
    setReviewBusy(true);
    try{
      const r=await kpFetch(`${API}/api/kp/submissions/${s.id}/review`,{
        method:'POST',headers:authHeaders(),body:JSON.stringify({decision,note,document_reviews:docReviews,data_review:dataReview})
      });
      const ct=r.headers.get('content-type')||'';
      const d=ct.includes('application/json')?await r.json().catch(()=>({})):{};
      if(!r.ok){
        const msg=d.msg||(r.status===502||r.status===503||r.status===504
          ?'Layanan verifikasi sementara tidak merespons. Tutup detail dan muat ulang status usulan sebelum mencoba lagi.'
          :`Verifikasi belum berhasil (HTTP ${r.status}). Periksa status usulan sebelum mengulangi.`);
        alert(msg);
        return;
      }
      alert(d.msg||'Verifikasi tersimpan.');onChanged();
    }catch(e){
      alert('Koneksi ke layanan verifikasi terputus. Periksa kembali status usulan sebelum mencoba lagi.');
    }finally{
      setReviewBusy(false);
    }
  };
  const defer=async()=>{
    const n=prompt('Alasan pengalihan ke TMT berikutnya:',note||'Berkas belum memenuhi ketentuan periode berjalan.');
    if(n===null)return;
    const r=await kpFetch(`${API}/api/kp/submissions/${s.id}/defer`,{method:'POST',headers:authHeaders(),body:JSON.stringify({note:n})});
    const d=await r.json();if(!r.ok)return alert(d.msg||'Pengajuan belum bisa dialihkan.');alert(d.msg);onChanged();
  };
  const overrideAk=async()=>{
    const n=prompt('Catatan persetujuan Angka Kredit (wajib):');if(!n)return;
    const r=await kpFetch(`${API}/api/kp/submissions/${s.id}/ak-override`,{method:'POST',headers:authHeaders(),body:JSON.stringify({note:n})});
    const d=await r.json();if(!r.ok)return alert(d.msg||'Angka Kredit belum bisa disetujui.');alert('Persetujuan Angka Kredit tersimpan.');onChanged();
  };

  const satlakStage = s.status === 'SUBMITTED';
  const sudinStage = s.status === 'SATLAK_APPROVED';
  const akReady=s.ak_override||String(s.ak_status||'').toLowerCase()==='cukup';
  const attention=[];
  if(missingRequired.length)attention.push(`Dokumen belum tersedia: ${missingRequired.map(x=>x.name).join(', ')}`);
  if(pakAnalysis.warnings.length)attention.push(`${pakAnalysis.warnings.length} catatan pada riwayat PAK`);
  if(dataChangeCount)attention.push(`${dataChangeCount} data diubah/dilengkapi oleh pengusul`);
  if(!akReady)attention.push('Angka Kredit perlu diperiksa');

  return <div className="fixed inset-0 z-[10000] bg-black/50 p-2 md:p-6 flex items-center justify-center">
    {reviewBusy && <div role="status" aria-live="polite" aria-busy="true" className="fixed inset-0 z-[11000] bg-slate-950/60 backdrop-blur-[2px] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center border border-slate-200">
        <div aria-hidden="true" className="w-14 h-14 mx-auto rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin" />
        <h3 className="font-bold text-lg text-slate-900 mt-5">{reviewAction || 'Memproses verifikasi...'}</h3>
        <p className="text-slate-600 text-sm leading-relaxed mt-2">Mohon tunggu. SIPAK sedang menyimpan keputusan petugas. Jangan klik tombol berulang atau menutup halaman.</p>
        <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">Sedang menghubungi server...</div>
      </div>
    </div>}
    <div className="bg-white w-full max-w-6xl max-h-[96vh] rounded-2xl overflow-hidden flex flex-col">
    <div className="p-5 border-b flex justify-between gap-3 bg-white"><div><div className="font-bold text-xl">{profile.full_name||'-'}</div><div className="text-sm text-slate-500 mt-1">{s.nip} · {profile.golongan||'-'} · {profile.unit_kerja||'-'} · {profile.kecamatan||'-'}</div><div className="mt-2"><StatusPill status={s.status}/></div></div><button onClick={onClose} className="text-2xl w-10 h-10 rounded-full hover:bg-slate-100">×</button></div>

    <div className="p-5 overflow-auto flex-1 bg-slate-50/50 space-y-4">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500">Data Pegawai</div><div className="font-bold mt-1">{dataChangeCount?<span className="text-amber-700">Perlu dicek</span>:<span className="text-emerald-700">Tidak ada perubahan</span>}</div><div className="text-xs text-slate-500 mt-1">{dataChangeCount?`${dataChangeCount} data berbeda dari data awal`:'Tidak ada perubahan data'}</div></div>
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500">Dokumen Wajib</div><div className="font-bold mt-1">{missingRequired.length?<span className="text-rose-700">Kurang {missingRequired.length}</span>:<span className="text-emerald-700">Lengkap</span>}</div><div className="text-xs text-slate-500 mt-1">{missingRequired.length?missingRequired.slice(0,3).map(x=>x.name).join(' · '):pendingDocReview.length?`${pendingDocReview.length} masih perlu diperiksa`:'Semua dokumen sudah diperiksa'}</div></div>
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500">Riwayat PAK</div><div className="font-bold mt-1">{pakAnalysis.count} PAK</div><div className={`text-xs mt-1 ${pakAnalysis.warnings.length?'text-amber-700':'text-emerald-700'}`}>{pakAnalysis.warnings.length?`${pakAnalysis.warnings.length} hal perlu diperhatikan`:'Urutan terbaca'}</div></div>
        <div className="bg-white border rounded-xl p-4"><div className="text-xs text-slate-500">Angka Kredit</div><div className={`font-bold mt-1 ${akReady?'text-emerald-700':'text-amber-700'}`}>{akReady?'Siap diperiksa':'Perlu dicek'}</div><div className="text-xs text-slate-500 mt-1">{s.ak_override?'Disetujui petugas':(s.ak_status||'Belum ada status')}</div></div>
      </div>

      {String(profile.jenis_kp||'').trim().toUpperCase()==='PI'&&<Banner type="warn"><b>KP PI — cek dokumen peningkatan pendidikan.</b> Pastikan Forlap DIKTI/PDDikti, Izin Belajar, dan Akreditasi sudah diunggah serta dinyatakan sesuai sebelum usulan diteruskan.</Banner>}
      {attention.length?<Banner type="warn"><b>Perlu perhatian sebelum diloloskan:</b><ul className="list-disc ml-5 mt-2">{attention.map((x,i)=><li key={i}>{x}</li>)}</ul></Banner>:<Banner type="ok"><b>Tidak ada hal khusus yang perlu diperhatikan.</b> Petugas tetap melakukan pemeriksaan sesuai ketentuan.</Banner>}

      <section className="bg-white border rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3"><div><div className="text-xs font-bold text-blue-600 uppercase tracking-wider">1 · Data Pegawai</div><h3 className="font-bold text-lg mt-1">Cocokkan data usulan</h3><p className="text-sm text-slate-500 mt-1">Data yang berbeda tidak otomatis salah. Periksa dengan dokumen pendukung.</p></div>{dataChangeCount?<Pill tone="yellow">{dataChangeCount} perubahan</Pill>:<Pill tone="green">Tidak berubah</Pill>}</div>
        <div className="mt-4"><DataGrid data={profile}/></div>
        {dataChangeCount>0&&<details className="border rounded-xl p-4 mt-4"><summary className="font-bold cursor-pointer">Lihat perbedaan dengan data awal</summary><div className="mt-3 space-y-2 text-sm">{data.data_changes.map((x,i)=><div key={i} className="grid md:grid-cols-[180px_1fr_1fr] gap-2 border-t pt-2"><b>{x.label}</b><span className="text-slate-500">Awal: {String(x.master||'-')}</span><span>Usulan: <b>{String(x.proposed||'-')}</b></span></div>)}</div></details>}
        <div className="border rounded-xl p-4 bg-slate-50 mt-4"><div className="font-bold">Hasil pemeriksaan data</div><div className="grid md:grid-cols-[220px_1fr] gap-3 mt-3"><select value={dataReview.status} onChange={e=>setDataReview({...dataReview,status:e.target.value})} className="border rounded-lg px-3 py-2"><option value="BELUM_DICEK">Belum diperiksa</option><option value="SESUAI">Sesuai</option><option value="BTS">Perlu diperbaiki</option></select><textarea value={dataReview.note} onChange={e=>setDataReview({...dataReview,note:e.target.value})} placeholder="Catatan bila ada data yang perlu diperbaiki" className="border rounded-lg p-2 min-h-[72px]"/></div></div>
      </section>

      <section className="bg-white border rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3"><div><div className="text-xs font-bold text-blue-600 uppercase tracking-wider">2 · Riwayat PAK</div><h3 className="font-bold text-lg mt-1">Dari paling lama ke paling terbaru</h3><p className="text-sm text-slate-500 mt-1">Jenis PAK mengikuti riwayat pegawai. Periksa apakah seluruh PAK yang seharusnya ada sudah dicantumkan dan urutannya benar.</p></div>{pakAnalysis.warnings.length?<Pill tone="yellow">Perlu perhatian</Pill>:<Pill tone="green">Urutan terbaca</Pill>}</div>
        <p className="text-xs text-slate-600 mt-3">Jika PAK memuat angka kredit peningkatan pendidikan, cocokkan jenis KP dengan dokumen pendukung. Jangan otomatis menetapkan PI hanya dari angka kredit; konfirmasi kepada pengusul/Suban.</p>
        {pakAnalysis.warnings.length>0&&<div className="mt-4"><Banner type="warn"><b>Catatan untuk petugas:</b><ul className="list-disc ml-5 mt-2">{pakAnalysis.warnings.map((x,i)=><li key={i}>{x}</li>)}</ul><div className="mt-2 text-sm">Catatan ini hanya membantu pemeriksaan. Cocokkan kembali dengan dokumen PAK sebelum mengambil keputusan.</div></Banner></div>}
        <div className="mt-4 space-y-0">{pakAnalysis.sorted.map((h,i)=><div key={h.id||i} className="relative pl-8 pb-5 last:pb-0"><div className="absolute left-[7px] top-2 bottom-0 w-px bg-slate-200 last:hidden"/><div className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-blue-600 border-4 border-blue-100"/><div className="border rounded-xl p-4"><div className="flex flex-wrap items-center justify-between gap-2"><div className="font-bold">{pakTypeLabel(h.pak_type)}</div><Pill tone="slate">PAK {i+1}</Pill></div><div className="text-sm text-slate-600 mt-1">{fmtDate(h.period_start)} s.d. {fmtDate(h.period_end)}</div><div className="text-sm mt-2">AK Akhir: <b>{h.ak_akhir}</b></div></div></div>)}{!pakAnalysis.count&&<Banner type="warn">Riwayat PAK belum diisi.</Banner>}</div>
      </section>

      <section className="bg-white border rounded-2xl p-5">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold text-blue-600 uppercase tracking-wider">3 · Dokumen</div>
            <h3 className="font-bold text-lg mt-1">Periksa berkas usulan</h3>
            <p className="text-sm text-slate-500 mt-1">Jika semua berkas yang tersedia sudah diperiksa dan sesuai, Bapak/Ibu dapat menandainya sekaligus.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {missingRequired.length?<Pill tone="red">Kurang {missingRequired.length}</Pill>:<Pill tone="green">Lengkap</Pill>}
            <button type="button" onClick={markAllDocumentsSuitable} disabled={!documents.length} className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"><CheckCircle2 size={15}/>Tandai Semua Sesuai</button>
          </div>
        </div>
        <div className="mt-4 space-y-3">{requirements.map(req=>{const d=documents.find(x=>x.document_code===req.code);return <div key={req.code} className={`border rounded-xl p-4 grid lg:grid-cols-[1fr_190px_1fr] gap-3 items-start ${!d&&req.required?'border-rose-200 bg-rose-50/30':''}`}><div><div className="flex flex-wrap items-center gap-2"><div className="font-bold">{req.name} {req.required&&<span className="text-rose-600">*</span>}</div>{!req.required&&<Pill tone="slate">Tidak wajib</Pill>}{d&&<Pill tone="green">Ada</Pill>}</div><div className="text-xs text-slate-500 mt-1">{req.hint}</div>{d&&<><a target="_blank" rel="noreferrer" href={d.drive_link} className="text-blue-600 text-xs mt-2 inline-block">Buka PDF · {fmtSize(d.size_bytes)}</a>{d.source_verified&&<div className="mt-1 text-xs text-emerald-700 font-semibold">✓ Pernah dinyatakan sesuai pada pengusulan sebelumnya.</div>}</>}</div><select value={docReviews[req.code]?.status||'BELUM_DICEK'} disabled={!d} onChange={e=>setDocReviews({...docReviews,[req.code]:{...(docReviews[req.code]||{}),status:e.target.value}})} className="border rounded-lg px-2 py-2"><option value="BELUM_DICEK">Belum diperiksa</option><option value="SESUAI">Sesuai</option><option value="BTS">Perlu diperbaiki</option></select><textarea disabled={!d} value={docReviews[req.code]?.note||''} onChange={e=>setDocReviews({...docReviews,[req.code]:{...(docReviews[req.code]||{}),note:e.target.value}})} placeholder="Catatan bila perlu diperbaiki" className="border rounded-lg p-2 min-h-[70px]"/></div>})}</div>
      </section>
    </div>

    <div className="p-5 border-t bg-white space-y-3">
      <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="Catatan untuk Bapak/Ibu Guru (isi bila ada yang perlu dijelaskan)" className="w-full border rounded-xl p-3 min-h-[70px]"/>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">{satlakStage ? 'Tahap saat ini: Satlak memeriksa usulan sebelum diteruskan ke Sudin.' : sudinStage ? 'Tahap saat ini: verifikasi akhir Sudin.' : 'Usulan tidak sedang berada pada tahap verifikasi aktif.'}</div>
        <div className="flex flex-wrap justify-end gap-2">
          {admin&&!s.ak_override&&String(s.ak_status).toLowerCase()!=='cukup'&&<button onClick={overrideAk} className="px-4 py-2 rounded-xl bg-amber-100 text-amber-800 font-bold">Setujui AK dengan Catatan</button>}
          <button onClick={defer} className="px-4 py-2 rounded-xl bg-slate-200 font-bold">Alihkan TMT</button>
          <button onClick={()=>review('BTS')} disabled={reviewBusy} className="px-4 py-2 rounded-xl bg-rose-600 text-white font-bold">Kembalikan untuk Perbaikan</button>
          {!admin && satlakStage && <button onClick={()=>review('APPROVED')} disabled={reviewBusy} className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold">Selesai Periksa · Kirim ke Sudin</button>}
          {admin && satlakStage && <button onClick={()=>review('SATLAK_APPROVED')} disabled={reviewBusy} className="px-4 py-2 rounded-xl bg-amber-500 text-white font-bold">Simulasikan Kirim ke Sudin</button>}
          {admin && sudinStage && <button onClick={()=>review('APPROVED')} disabled={reviewBusy} className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold">Verifikasi Sudin</button>}
        </div>
      </div>
    </div>
  </div></div>;
}

function DataGrid({data}) {
  const items=[
    ['Nama',data.full_name],['NIP',data.nip],['NRK',data.nrk],['Golongan',data.golongan],
    ['TMT Golongan',fmtDate(data.tmt_golongan)],['Masa Kerja Tahun',data.masa_kerja_tahun],['Masa Kerja Bulan',data.masa_kerja_bulan],
    ['Pendidikan Terakhir',data.pendidikan_terakhir],['Jabatan',data.jabatan],['Nama Atasan',data.nama_atasan],['NIP Atasan',data.nip_atasan],
    ['Unit Kerja',data.unit_kerja],['Kecamatan',data.kecamatan],['UKPD',data.ukpd],['Jenis Kenaikan Pangkat',data.jenis_kp],
    ['Nilai SKP 2025',data.nilai_skp_2025],['Nilai SKP 2024',data.nilai_skp_2024]
  ];
  return <div className="grid md:grid-cols-2 gap-3">{items.map(([k,v])=><div key={k} className="border rounded-xl p-3"><div className="text-xs text-slate-500">{k}</div><div className="font-semibold mt-1">{v===0?0:(v||'-')}</div></div>)}</div>;
}

function SubmissionField({label,name,type='text',required=true,placeholder='',children,data,disabled,update,changed,baseValue,cls}) {
  return <div>
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <label className="text-xs font-bold text-slate-600">{label}{required&&<span className="text-rose-500"> *</span>}</label>
      {changed(name)&&<span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">DIUBAH</span>}
    </div>
    {children||<input disabled={disabled} type={type} value={data?.[name]??''} onChange={e=>update(name,e.target.value)} placeholder={placeholder} className={cls(name,required)}/>} 
    {changed(name)&&<div className="text-[11px] text-slate-400 mt-1">Sebelumnya: {baseValue(name)}</div>}
  </div>;
}

function EditableSubmissionData({data,setData,master,disabled,onDirty}) {
  const golonganOptions=['III/a','III/b','III/c','III/d','IV/a','IV/b','IV/c','IV/d','IV/e'];
  const kecamatanOptions=['Cipayung','Ciracas','Kramat Jati','Makasar','Pasar Rebo'];
  const jenisOptions=['PI','PP','REG','JF'];
  const skpOptions=['Baik','Sangat Baik'];

  const requiredKeys=[
    'full_name','nip','nrk','golongan','tmt_golongan','pendidikan_terakhir','jabatan',
    'nama_atasan','nip_atasan','unit_kerja','kecamatan','ukpd','jenis_kp','nilai_skp_2025','nilai_skp_2024'
  ];
  const filled=requiredKeys.filter(k=>String(data?.[k]??'').trim()).length;
  const complete=filled===requiredKeys.length;

  const update=(key,value)=>{
    setData(prev=>({...prev,[key]:value}));
    onDirty?.();
  };

  const changed=(key)=>String(data?.[key]??'').trim()!==String(master?.[key]??'').trim();
  const baseValue=(key)=>{
    const v=master?.[key];
    if(key==='tmt_golongan') return fmtDate(v);
    return v===0?'0':(v||'-');
  };
  const cls=(key,required=true)=>`w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-200 ${required&&!String(data?.[key]??'').trim()?'border-amber-300 bg-amber-50':'border-slate-200 bg-white'}`;
  const common={data,disabled,update,changed,baseValue,cls};

  return <div>
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
      <div className="text-sm text-slate-600">Data terisi: <b>{filled}/{requiredKeys.length}</b></div>
      <Pill tone={complete?'green':'yellow'}>{complete?'Data lengkap':`${requiredKeys.length-filled} data belum diisi`}</Pill>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <SubmissionField {...common} label="Nama" name="full_name" placeholder="Nama lengkap sesuai dokumen"/>
      <SubmissionField {...common} label="NIP" name="nip" placeholder="18 digit NIP"/>
      <SubmissionField {...common} label="NRK" name="nrk" placeholder="NRK"/>
      <SubmissionField {...common} label="Golongan" name="golongan"><select disabled={disabled} value={data?.golongan||''} onChange={e=>update('golongan',e.target.value)} className={cls('golongan')}><option value="">Pilih golongan</option>{golonganOptions.map(x=><option key={x} value={x}>{x}</option>)}</select></SubmissionField>
      <SubmissionField {...common} label="TMT Golongan" name="tmt_golongan" type="date"/>
      <div className="grid grid-cols-2 gap-3"><SubmissionField {...common} label="Masa Kerja Tahun" name="masa_kerja_tahun" type="number" required={false}/><SubmissionField {...common} label="Masa Kerja Bulan" name="masa_kerja_bulan" type="number" required={false}/></div>
      <SubmissionField {...common} label="Pendidikan Terakhir" name="pendidikan_terakhir"/>
      <SubmissionField {...common} label="Jabatan" name="jabatan"/>
      <SubmissionField {...common} label="Nama Atasan" name="nama_atasan"/>
      <SubmissionField {...common} label="NIP Atasan" name="nip_atasan" placeholder="18 digit NIP atasan"/>
      <SubmissionField {...common} label="Unit Kerja" name="unit_kerja"/>
      <SubmissionField {...common} label="Kecamatan" name="kecamatan"><select disabled={disabled} value={data?.kecamatan||''} onChange={e=>update('kecamatan',e.target.value)} className={cls('kecamatan')}><option value="">Pilih kecamatan</option>{kecamatanOptions.map(x=><option key={x} value={x}>{x}</option>)}</select></SubmissionField>
      <SubmissionField {...common} label="UKPD" name="ukpd"/>
      <SubmissionField {...common} label="Jenis Kenaikan Pangkat" name="jenis_kp"><select disabled={disabled} value={data?.jenis_kp||''} onChange={e=>update('jenis_kp',e.target.value)} className={cls('jenis_kp')}><option value="">Pilih jenis</option>{jenisOptions.map(x=><option key={x} value={x}>{x}</option>)}</select></SubmissionField>
      {String(data?.jenis_kp||'').trim().toUpperCase()==='PI'&&<div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><b>Persyaratan khusus KP Peningkatan Pendidikan (PI)</b><div className="mt-1">Pada tahap Dokumen, siapkan bukti Forlap DIKTI/PDDikti, Surat Izin Belajar, dan bukti Akreditasi. Simpan data ini dahulu agar daftar upload menyesuaikan.</div></div>}
      <SubmissionField {...common} label="Nilai SKP 2025" name="nilai_skp_2025"><select disabled={disabled} value={data?.nilai_skp_2025||''} onChange={e=>update('nilai_skp_2025',e.target.value)} className={cls('nilai_skp_2025')}><option value="">Pilih nilai</option>{skpOptions.map(x=><option key={x} value={x}>{x}</option>)}</select></SubmissionField>
      <SubmissionField {...common} label="Nilai SKP 2024" name="nilai_skp_2024"><select disabled={disabled} value={data?.nilai_skp_2024||''} onChange={e=>update('nilai_skp_2024',e.target.value)} className={cls('nilai_skp_2024')}><option value="">Pilih nilai</option>{skpOptions.map(x=><option key={x} value={x}>{x}</option>)}</select></SubmissionField>
    </div>

    <div className="mt-4 text-xs text-slate-500">Beberapa kolom untuk petugas akan diisi otomatis oleh sistem saat proses pemeriksaan.</div>
  </div>;
}

function TeacherPage({ testMode = false }) {
  const [ctx,setCtx]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [history,setHistory]=useState([]);
  const [note,setNote]=useState('');
  const [formData,setFormData]=useState({});
  const [dataDirty,setDataDirty]=useState(false);
  const [savingData,setSavingData]=useState(false);
  const [savingHistory,setSavingHistory]=useState(false);
  const [historyDirty,setHistoryDirty]=useState(false);
  const [dataMessage,setDataMessage]=useState('');
  const [step,setStep]=useState(1);
  const [showWelcome,setShowWelcome]=useState(true);
  const [showSubmitConfirm,setShowSubmitConfirm]=useState(false);
  const testTeacherName = sessionStorage.getItem('kpTestTeacherName') || 'Guru SIPAK';

  const exitTest = () => {
    sessionStorage.removeItem('kpTestUserId');
    sessionStorage.removeItem('kpTestTeacherName');
    window.location.reload();
  };

  const load=async({silent=false,keepWelcome=false}={})=>{
    if(!silent)setLoading(true);
    try{
      const r=await kpFetch(`${API}/api/kp/my/context`,{headers:authHeaders(false)});
      const d=await r.json();
      if(!r.ok){setError(d.msg||'Data belum bisa dibuka.');return;}
      setCtx({...d,existing_documents:[],source_availability:'CHECKING'});
      // Metadata Drive diverifikasi terpisah agar halaman profil tidak menunggu banyak API Google.
      kpFetch(`${API}/api/kp/my/document/available-sources?period_id=${encodeURIComponent(d.period.id)}`,{headers:authHeaders(false)})
        .then(async result=>{
          const availability=await result.json().catch(()=>({}));
          if(!result.ok)throw new Error(availability.msg||'Sumber belum dapat diperiksa.');
          setCtx(current=>current?.submission?.id===d.submission?.id
            ? {...current,existing_documents:availability.existing_documents||[],
                source_availability:'READY',source_hidden_count:availability.hidden_document_count||0,
                source_temporary_error_count:availability.temporary_error_count||0,
                source_unverified_count:availability.unverified_count||0}
            : current);
        }).catch(()=>setCtx(current=>current?.submission?.id===d.submission?.id
          ? {...current,existing_documents:[],source_availability:'ERROR'}:current));
      setFormData(d.form_data||d.simpeg||d.profile||{});
      setDataDirty(false);
      setHistory((d.pak_history||[]).map(h=>({
        pak_type:h.pak_type||'',
        period_start:String(h.period_start||'').slice(0,10),
        period_end:String(h.period_end||'').slice(0,10),
        ak_akhir:h.ak_akhir
      })));
      setHistoryDirty(false);
      setNote(d.submission?.teacher_note||'');
      if(!keepWelcome){
        setShowWelcome(
          d.submission?.status === 'DRAFT' &&
          (d.documents || []).length === 0 &&
          (d.pak_history || []).length === 0
        );
      }
    }finally{
      if(!silent)setLoading(false);
    }
  };
  useEffect(()=>{load();},[]);
  if(loading)return <Loading label="Menyiapkan pengajuan..."/>;
  if(error)return <CenteredError message={error}/>;

  const s=ctx.submission;
  const submitted=!['DRAFT','BTS'].includes(s.status);
  const docs=ctx.documents||[];
  const missing=ctx.requirements.filter(r=>r.required&&!docs.some(d=>d.document_code===r.code));
  const akOk=s.ak_override||String(ctx.pak_status?.status_ak||'').toLowerCase()==='cukup';
  const historyOk=history.length>0&&!historyDirty;
  const pakAnalysis=analyzePakRows(history);
  const dataOk=Boolean(ctx.form_validation?.ok)&&!dataDirty;
  const canSubmit=dataOk&&akOk&&missing.length===0&&historyOk&&!submitted;
  const progressDone=[dataOk,akOk&&historyOk,missing.length===0,canSubmit||submitted];

  const saveData=async()=>{
    setSavingData(true);
    setDataMessage('');
    try{
      const r=await kpFetch(`${API}/api/kp/my/data`,{method:'PUT',headers:authHeaders(),body:JSON.stringify({period_id:ctx.period.id,data:formData})});
      const d=await r.json();
      if(!r.ok){
        setDataMessage(d.msg||'Data belum dapat disimpan.');
        return;
      }
      setDataMessage(d.msg||'Data sudah disimpan.');
      await load({silent:true,keepWelcome:true});
    }catch(e){
      setDataMessage('Gagal menyimpan data. Periksa koneksi lalu coba lagi.');
    }finally{
      setSavingData(false);
    }
  };

  const saveHistory=async(rowsOverride=null)=>{
    if(savingHistory)return;
    const rowsToSave=sortPakRows(Array.isArray(rowsOverride)?rowsOverride:history);
    setHistory(rowsToSave);
    setSavingHistory(true);
    try{
      const r=await kpFetch(`${API}/api/kp/my/pak-history`,{method:'PUT',headers:authHeaders(),body:JSON.stringify({period_id:ctx.period.id,rows:rowsToSave})});
      const d=await r.json();
      if(!r.ok)return alert(d.msg||'PAK belum bisa disimpan.');
      await load({silent:true,keepWelcome:true});
      setStep(3);
    }finally{
      setSavingHistory(false);
    }
  };

  const doSubmit=async()=>{
    setShowSubmitConfirm(false);
    const r=await kpFetch(`${API}/api/kp/my/submit`,{method:'POST',headers:authHeaders(),body:JSON.stringify({period_id:ctx.period.id,teacher_note:note})});
    const d=await r.json();
    if(!r.ok){
      if(d.code==='AK_NOT_ENOUGH')alert(`ANGKA KREDIT BELUM MENCUKUPI\n\n${d.msg}`);
      else if(d.code==='DATA_NOT_COMPLETE')alert(`DATA BELUM LENGKAP\n\n${d.msg}`);
      else alert(d.msg||'Pengajuan belum dapat dikirim.');
      return;
    }
    alert('Pengajuan berhasil dikirim dan akan diperiksa Satlak.');
    await load({silent:true,keepWelcome:true});
  };

  const steps=[
    [1,'Cek Data'],
    [2,'Angka Kredit & PAK'],
    [3,'Dokumen'],
    [4,'Cek & Kirim']
  ];
  const stepEnabled={
    1:true,
    2:dataOk||submitted,
    3:(dataOk&&historyOk)||submitted,
    4:(dataOk&&historyOk&&missing.length===0)||submitted
  };

  return <PageShell
    title={testMode ? `POV Guru · ${testTeacherName}` : 'Usulan Kenaikan Pangkat'}
    subtitle={`Periode pengusulan ${fmtDate(ctx.period.submission_open_at)} – ${fmtDate(ctx.period.submission_close_at)} untuk TMT ${fmtDate(ctx.period.tmt_date)}.`}
    right={<div className="flex flex-wrap gap-2 items-center"><StatusPill status={s.status}/>{testMode&&<button type="button" onClick={exitTest} className="px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold">Keluar Simulasi</button>}</div>}
  >
    <div className="space-y-5">
      {testMode&&<Banner type="warn"><b>MODE SIMULASI ADMIN.</b> Kamu sedang melihat halaman yang nantinya dilihat oleh <b>{testTeacherName}</b>. Pengujian ini tidak memakai password guru.</Banner>}

      {showWelcome && !submitted && <div className="bg-white border rounded-2xl p-6 md:p-8 shadow-sm">
        <div className="max-w-3xl">
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider">Pengusulan Kenaikan Pangkat</div>
          <h2 className="text-2xl font-bold mt-2">Cek data dan berkas sebelum dikirim.</h2>
          <p className="text-slate-600 mt-3 leading-relaxed">Data yang sudah ada akan muncul otomatis. Kalau ada yang kosong atau salah, silakan diperbaiki. Masukkan PAK yang memang Bapak/Ibu miliki, lengkapi berkas, lalu kirim.</p>
          <div className="mt-5 grid sm:grid-cols-4 gap-3 text-sm"><div className="border rounded-xl p-4"><b>1. Cek data</b><div className="text-slate-500 mt-1">Isi yang kosong atau perbaiki yang salah.</div></div><div className="border rounded-xl p-4"><b>2. Isi PAK</b><div className="text-slate-500 mt-1">Masukkan PAK yang dimiliki. SIPAK akan menyusun dari yang paling lama.</div></div><div className="border rounded-xl p-4"><b>3. Berkas</b><div className="text-slate-500 mt-1">Pakai berkas lama atau upload PDF baru.</div></div><div className="border rounded-xl p-4"><b>4. Kirim</b><div className="text-slate-500 mt-1">Cek sekali lagi, lalu kirim.</div></div></div>
          <button onClick={()=>setShowWelcome(false)} className="mt-6 px-5 py-3 rounded-xl bg-blue-600 text-white font-bold inline-flex gap-2 items-center">Mulai <ArrowRight size={17}/></button>
        </div>
      </div>}

      {(!showWelcome || submitted) && <>
        <div className="bg-white border rounded-2xl p-3 md:p-4 overflow-x-auto"><div className="flex min-w-[680px] items-center">{steps.map(([num,label],i)=><React.Fragment key={num}><button type="button" disabled={!stepEnabled[num]} onClick={()=>stepEnabled[num]&&setStep(num)} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold ${step===num?'bg-slate-900 text-white':stepEnabled[num]?'text-slate-600 hover:bg-slate-100':'text-slate-300 cursor-not-allowed'}`}><span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs ${progressDone[i]?'bg-emerald-500 text-white':step===num?'bg-white text-slate-900':'bg-slate-200'}`}>{progressDone[i]?'✓':num}</span>{label}</button>{i<steps.length-1&&<div className="h-px bg-slate-200 flex-1 mx-2"/>}</React.Fragment>)}</div></div>

        {s.status==='BTS'&&<Banner type="error"><b>Pengajuan perlu diperbaiki.</b><div className="mt-1">{s.reviewer_note||'Silakan lihat catatan pada dokumen yang ditandai dan perbaiki hanya bagian tersebut. Dokumen lain yang sudah benar tidak perlu diunggah ulang.'}</div></Banner>}
        {s.status==='SUBMITTED'&&<Banner type="info"><b>Pengajuan sudah dikirim.</b> Saat ini menunggu pemeriksaan Satlak. Bapak/Ibu tidak perlu mengirim ulang.</Banner>}
        {s.status==='SATLAK_APPROVED'&&<Banner type="ok"><b>Sudah diperiksa Satlak.</b> Sekarang menunggu pemeriksaan Sudin.</Banner>}
        {s.status==='APPROVED'&&<Banner type="ok"><b>Pengajuan sudah diperiksa Sudin.</b> Bapak/Ibu tidak perlu melakukan apa-apa lagi saat ini.</Banner>}
        {s.status==='DEFERRED'&&<Banner type="warn"><b>Pengajuan dialihkan ke TMT berikutnya.</b> {s.btl_note||'Silakan ikuti informasi periode berikutnya.'}</Banner>}

        {step===1&&<div className="bg-white border rounded-2xl p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-3 mb-4"><div><h2 className="font-bold text-xl">1. Cek Data</h2><p className="text-sm text-slate-600 mt-1">Periksa data di bawah. Kalau ada yang kosong atau salah, silakan isi atau perbaiki.</p></div>{dataOk?<Pill tone="green">Data lengkap</Pill>:<Pill tone="yellow">Perlu dilengkapi</Pill>}</div>
          <Banner type="info">Perubahan di halaman ini hanya dipakai untuk pengajuan kenaikan pangkat ini. Profil SIPAK utama tidak berubah.</Banner>
          {(ctx.data_changes||[]).length>0&&!dataDirty&&<div className="mt-3"><Banner type="warn">Ada {(ctx.data_changes||[]).length} data yang Bapak/Ibu ubah. Petugas akan melihat perubahan tersebut saat memeriksa pengajuan.</Banner></div>}
          <div className="mt-5"><EditableSubmissionData data={formData} setData={setFormData} master={ctx.simpeg||ctx.profile||{}} disabled={submitted} onDirty={()=>{setDataDirty(true);setDataMessage('');}}/></div>
          {dataMessage&&<div className="mt-4"><Banner type={dataOk?'ok':'warn'}>{dataMessage}</Banner></div>}
          {!submitted&&<div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center justify-between"><div className="text-xs text-slate-500">Kolom berwarna kuning masih perlu diisi.</div><div className="flex gap-2"><button type="button" disabled={!dataDirty||savingData} onClick={saveData} className="px-5 py-2.5 rounded-xl bg-slate-900 disabled:bg-slate-300 text-white font-bold inline-flex items-center gap-2"><Save size={16}/>{savingData?'Menyimpan...':'Simpan'}</button><button disabled={!dataOk} onClick={()=>setStep(2)} className="px-5 py-2.5 rounded-xl bg-blue-600 disabled:bg-slate-300 text-white font-bold inline-flex items-center justify-center gap-2">Lanjut <ArrowRight size={16}/></button></div></div>}
        </div>}

        {step===2&&<div className="space-y-4">
          <div className="bg-white border rounded-2xl p-5"><h2 className="font-bold text-xl">2. Angka Kredit & Riwayat PAK</h2><p className="text-sm text-slate-600 mt-1">Masukkan riwayat PAK sesuai dokumen yang Bapak/Ibu miliki. Jika pernah memiliki PAK sebelumnya, jangan dilewati. SIPAK akan menyusun otomatis dari periode paling lama ke paling terbaru.</p><div className="mt-4">{akOk?<Banner type="ok"><b>Angka Kredit tercatat cukup.</b> Petugas tetap akan memeriksa kembali.</Banner>:<Banner type="error"><b>Angka Kredit belum tercatat cukup.</b><br/>Silakan hubungi petugas jika data ini belum sesuai.</Banner>}</div></div>
          <PakHistoryEditor rows={history} setRows={setHistory} disabled={submitted} onSave={saveHistory} saving={savingHistory} onDirty={()=>setHistoryDirty(true)}/>
        </div>}

        {step===3&&<div className="bg-white border rounded-2xl p-5 md:p-6"><h2 className="font-bold text-xl">3. Lengkapi Dokumen</h2><p className="text-sm text-slate-600 mt-1">Pakai dokumen yang sudah ada atau unggah PDF baru. Maksimal 900 KB per file.</p><Banner type="info">Dokumen lama boleh dipakai lagi. Kalau ingin memastikan isinya, klik <b>Lihat</b> sebelum memilih.</Banner>{ctx.source_availability==='CHECKING'&&<Banner type="info">Memeriksa PDF lama di Google Drive...</Banner>}{ctx.source_availability==='ERROR'&&<Banner type="warn">Pemeriksaan arsip sementara gagal. Bapak/Ibu tetap dapat mengunggah PDF baru.</Banner>}{ctx.source_availability==='READY'&&Number(ctx.source_hidden_count)>0&&<Banner type="warn">{ctx.source_hidden_count} dokumen lama disembunyikan karena tidak dapat diakses atau tidak memenuhi ketentuan PDF. Silakan unggah PDF baru bila diperlukan.</Banner>}{ctx.source_availability==='READY'&&Number(ctx.source_unverified_count)>0&&<Banner type="warn">Sebagian arsip lama belum diperiksa. Hubungi petugas jika dokumen yang dicari belum muncul.</Banner>}{/* SIPAK_KP_LAUNCH_FIX_V3 */}<div className="mt-4 space-y-3">{ctx.requirements.map(req=><RequirementRow key={req.code} req={req} ctx={ctx} disabled={submitted} onChanged={()=>load({silent:true,keepWelcome:true})}/>)}</div>{!submitted&&<div className="mt-5 flex flex-col sm:flex-row justify-between gap-3 sm:items-center"><div className="text-sm text-slate-500">{missing.length?`${missing.length} dokumen wajib belum diisi.`:'Semua dokumen wajib sudah ada.'}</div><button onClick={()=>{if(missing.length){alert(`Masih ada ${missing.length} dokumen wajib yang belum diisi:

${missing.map(x=>`• ${x.name}`).join('\n')}`);return;}setStep(4);}} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-bold inline-flex items-center justify-center gap-2">Cek Sebelum Kirim <ArrowRight size={16}/></button></div>}</div>}

        {step===4&&<div className="space-y-4">
          <div className="bg-white border rounded-2xl p-5 md:p-6"><h2 className="font-bold text-xl">4. Cek Sebelum Kirim</h2><p className="text-sm text-slate-600 mt-1">Pastikan semua bagian sudah lengkap sebelum dikirim.</p><div className="mt-5 space-y-2"><CheckLine ok={dataOk} text="Data sudah lengkap"/><CheckLine ok={akOk} text="Angka Kredit tercatat cukup"/><CheckLine ok={historyOk} text={historyOk?`${pakAnalysis.count} riwayat PAK sudah tersimpan dan disusun kronologis`:`Riwayat PAK belum tersimpan`}/><CheckLine ok={missing.length===0} text={missing.length===0?'Semua dokumen wajib sudah tersedia':`${missing.length} dokumen wajib belum tersedia`}/></div>{missing.length>0&&<div className="mt-4"><Banner type="warn"><b>Masih perlu dilengkapi:</b><ul className="list-disc ml-5 mt-2">{missing.map(x=><li key={x.code}>{x.name}</li>)}</ul></Banner></div>}</div>
          <div className="bg-white border rounded-2xl p-5"><label className="text-sm font-bold">Catatan untuk Satlak (opsional)</label><textarea disabled={submitted} value={note} onChange={e=>setNote(e.target.value)} className="w-full border rounded-xl p-3 mt-2 min-h-[80px]" placeholder="Contoh: SK Pangkat memakai dokumen yang sudah pernah diunggah."/><div className="mt-4 flex flex-col sm:flex-row justify-between gap-3 sm:items-center"><div className={`text-sm ${canSubmit?'text-emerald-700':'text-slate-500'}`}>{canSubmit?'Semua sudah lengkap.':'Lengkapi bagian yang masih merah.'}</div><button disabled={!canSubmit} onClick={()=>setShowSubmitConfirm(true)} className="px-5 py-2.5 rounded-xl bg-blue-600 disabled:bg-slate-300 text-white font-bold inline-flex items-center justify-center gap-2"><Send size={16}/>Kirim Usulan</button></div></div>
        </div>}
      </>}

      {showSubmitConfirm&&<div className="fixed inset-0 z-[12000] bg-black/50 flex items-center justify-center p-4"><div className="bg-white max-w-lg w-full rounded-2xl p-6 shadow-xl"><div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center"><Send size={20}/></div><h3 className="text-xl font-bold mt-4">Kirim pengajuan sekarang?</h3><p className="text-sm text-slate-600 mt-2">Setelah dikirim, pengajuan akan diperiksa oleh Satlak. Jika ada yang perlu diperbaiki, Bapak/Ibu akan mendapat catatan dari petugas.</p><div className="mt-4 bg-slate-50 border rounded-xl p-3 text-sm"><b>TMT:</b> {fmtDate(ctx.period.tmt_date)}<br/><b>Dokumen:</b> lengkap<br/><b>PAK:</b> sudah diisi</div><div className="mt-5 flex justify-end gap-2"><button onClick={()=>setShowSubmitConfirm(false)} className="px-4 py-2 rounded-xl bg-slate-100 font-bold">Periksa Lagi</button><button onClick={doSubmit} className="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold">Ya, Kirim</button></div></div></div>}
    </div>
  </PageShell>;
}

function CheckLine({ok,text}) {
  return <div className={`flex items-center gap-3 p-3 rounded-xl border ${ok?'border-emerald-200 bg-emerald-50':'border-rose-200 bg-rose-50'}`}>{ok?<CheckCircle2 size={19} className="text-emerald-600"/>:<XCircle size={19} className="text-rose-600"/>}<span className="text-sm font-semibold">{text}</span></div>;
}

function PakHistoryEditor({rows,setRows,disabled,onSave,saving=false,onDirty}) {
  const add=()=>{setRows([...rows,{pak_type:'',period_start:'',period_end:'',ak_akhir:''}]);onDirty?.();};
  const update=(i,k,v)=>{setRows(rows.map((r,idx)=>idx===i?{...r,[k]:v}:r));onDirty?.();};
  const analysis=analyzePakRows(rows);
  const save=()=>{
    const sorted=sortPakRows(rows);
    setRows(sorted);
    onSave?.(sorted);
  };
  return <div className="bg-white border rounded-2xl p-5">
    <div className="flex flex-col md:flex-row md:justify-between gap-3 md:items-start"><div><h2 className="font-bold text-lg">Riwayat PAK</h2><p className="text-sm text-slate-600 mt-1">Isi PAK yang Bapak/Ibu miliki. Jenisnya boleh Konvensional, Integrasi, Konversi, atau hanya salah satunya. Saat disimpan, SIPAK otomatis menyusun berdasarkan periode paling lama ke paling terbaru.</p></div>{!disabled&&<button type="button" onClick={add} className="px-3 py-2 rounded-lg bg-slate-100 font-bold inline-flex items-center gap-1 self-start"><Plus size={15}/>Tambah PAK</button>}</div>
    {analysis.count>0&&<div className="mt-4"><Banner type={analysis.warnings.length?'warn':'info'}>{analysis.warnings.length?<><b>{analysis.warnings.length} hal perlu diperhatikan.</b> Data tetap bisa disimpan dan akan diperiksa petugas.</>:<><b>Urutan akan diatur otomatis.</b> Bapak/Ibu tidak perlu memindahkan baris secara manual.</>}</Banner></div>}
    <div className="mt-4 space-y-3">{rows.map((r,i)=><div key={i} className="border rounded-xl p-4"><div className="font-bold text-sm mb-3">PAK {i+1}{r.pak_type?` · ${pakTypeLabel(r.pak_type)}`:''}</div><div className="grid md:grid-cols-[1.1fr_1fr_1fr_0.8fr_52px] gap-3 items-end"><div><label className="text-xs font-semibold text-slate-600">Jenis PAK</label><select disabled={disabled} value={r.pak_type||''} onChange={e=>update(i,'pak_type',e.target.value)} className="w-full border rounded-lg p-2 mt-1"><option value="">Pilih jenis PAK</option><option value="KONVENSIONAL">PAK Konvensional</option><option value="INTEGRASI">PAK Integrasi</option><option value="KONVERSI">PAK Konversi</option></select></div><div><label className="text-xs font-semibold text-slate-600">Periode Mulai</label><input disabled={disabled} type="date" value={String(r.period_start||'').slice(0,10)} onChange={e=>update(i,'period_start',e.target.value)} className="w-full border rounded-lg p-2 mt-1"/></div><div><label className="text-xs font-semibold text-slate-600">Periode Selesai</label><input disabled={disabled} type="date" value={String(r.period_end||'').slice(0,10)} onChange={e=>update(i,'period_end',e.target.value)} className="w-full border rounded-lg p-2 mt-1"/></div><div><label className="text-xs font-semibold text-slate-600">AK Akhir</label><input disabled={disabled} type="number" inputMode="decimal" step="0.001" min="0" value={r.ak_akhir??''} onChange={e=>update(i,'ak_akhir',e.target.value)} placeholder="Contoh: 50" className="w-full border rounded-lg p-2 mt-1"/></div>{!disabled&&<button type="button" title="Hapus PAK" onClick={()=>{setRows(rows.filter((_,idx)=>idx!==i));onDirty?.();}} className="h-10 rounded-lg bg-rose-50 text-rose-700"><Trash2 size={15} className="mx-auto"/></button>}</div></div>)}{!rows.length&&<div className="border border-dashed rounded-xl p-5 text-sm text-slate-500">Belum ada riwayat PAK. Klik <b>Tambah PAK</b> dan isi sesuai dokumen yang Bapak/Ibu miliki. Jika ada PAK sebelumnya, jangan dilewati.</div>}</div>
    {!disabled&&<button type="button" disabled={saving} onClick={save} className="mt-4 px-4 py-2.5 rounded-xl bg-slate-900 disabled:bg-slate-300 text-white font-bold inline-flex items-center gap-2"><Save size={16}/>{saving?'Menyimpan...':'Simpan & Lanjut'}</button>}
  </div>;
}

function RequirementRow({req,ctx,disabled,onChanged}) {
  const doc=ctx.documents.find(d=>d.document_code===req.code);
  const [source,setSource]=useState('');
  const [newFile,setNewFile]=useState(null);
  const [busy,setBusy]=useState(false);
  const [pakConfirmStep,setPakConfirmStep]=useState(0);
  const allCandidates=ctx.existing_documents||[];
  const candidates=candidatesForRequirement(req.code, allCandidates);
  const selectedCandidate=candidates.find(c=>`${c.source_kind}:${c.source_id}`===source);
  const isPakGabungan=req.code==='PAK_GABUNGAN';

  const openPdf=async url=>{
    const w=window.open('', '_blank');
    try{
      const r=await kpFetch(url,{headers:authHeaders(false)});
      if(!r.ok){const d=await r.json().catch(()=>({}));if(w)w.close();return alert(d.msg||'Dokumen belum bisa dibuka.');}
      const blob=await r.blob();
      const blobUrl=URL.createObjectURL(blob);
      if(w)w.location.href=blobUrl;else window.open(blobUrl,'_blank');
      setTimeout(()=>URL.revokeObjectURL(blobUrl),60000);
    }catch(e){if(w)w.close();alert('Dokumen belum bisa dibuka. Silakan coba lagi.');}
  };

  const validateFile=()=>{
    const file=newFile;
    if(!file){alert('Pilih file PDF terlebih dahulu.');return false;}
    if(file.type && file.type!=='application/pdf' && !/\.pdf$/i.test(file.name)){alert('File harus PDF.');return false;}
    if(file.size>MAX_FILE_BYTES){alert(`Ukuran file ${fmtSize(file.size)}. Maksimal 900 KB.`);return false;}
    return true;
  };

  const performUpload=async()=>{
    if(!validateFile())return;
    const file=newFile;
    setBusy(true);
    try{
      const fd=new FormData();fd.append('file',file);fd.append('period_id',ctx.period.id);
      const r=await kpFetch(`${API}/api/kp/my/document/upload/${req.code}`,{method:'POST',headers:authHeaders(false),body:fd});
      const d=await r.json();
      if(!r.ok)return alert(d.msg||'File belum berhasil diunggah.');
      setNewFile(null);
      setPakConfirmStep(0);
      await onChanged?.();
      if(isPakGabungan)alert('PAK Gabungan berhasil diunggah. Petugas akan memeriksa isi dan urutannya pada tahap verifikasi.');
    }finally{setBusy(false);}
  };

  const upload=async()=>{
    if(!validateFile())return;
    if(isPakGabungan){setPakConfirmStep(1);return;}
    const file=newFile;
    if(!window.confirm(`Upload file ini untuk ${req.name}?\n\n${file.name}\n${fmtSize(file.size)}`))return;
    await performUpload();
  };

  const reuse=async()=>{
    if(!source)return alert('Pilih dokumen lama terlebih dahulu.');
    const extra=isPakGabungan?'\n\nPastikan file tersebut sudah berisi PAK yang diperlukan dan urutannya dari yang paling lama ke paling terbaru.':'';
    if(!window.confirm(`Pakai dokumen ini untuk ${req.name}?\n\n${selectedCandidate?.name||''}${extra}`))return;
    const [kind,id]=source.split(':');setBusy(true);
    try{
      const r=await kpFetch(`${API}/api/kp/my/document/reuse/${req.code}`,{method:'POST',headers:authHeaders(),body:JSON.stringify({source_kind:kind,source_id:id,period_id:ctx.period.id})});
      const d=await r.json();if(!r.ok)return alert(d.msg||'Dokumen belum bisa dipakai.');
      await onChanged?.();
    }finally{setBusy(false);}
  };

  const remove=async()=>{
    if(!window.confirm('Ganti dokumen ini?'))return;
    setBusy(true);
    try{
      const r=await kpFetch(`${API}/api/kp/my/document/${req.code}?period_id=${ctx.period.id}`,{method:'DELETE',headers:authHeaders(false)});
      const d=await r.json().catch(()=>({}));if(!r.ok)return alert(d.msg||'Dokumen belum bisa diganti.');
      await onChanged?.();
    }finally{setBusy(false);}
  };

  const PakConfirmModal=()=>pakConfirmStep>0?<div className="fixed inset-0 z-[13000] bg-black/55 flex items-center justify-center p-4"><div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
    <div className="p-6">
      <div className="flex items-center justify-between gap-3"><div className="text-xs font-bold text-blue-600 uppercase tracking-wider">PAK Gabungan · Langkah {pakConfirmStep} dari 3</div><button type="button" onClick={()=>setPakConfirmStep(0)} className="text-2xl text-slate-400">×</button></div>
      <div className="flex gap-2 mt-3">{[1,2,3].map(n=><div key={n} className={`h-1.5 flex-1 rounded-full ${n<=pakConfirmStep?'bg-blue-600':'bg-slate-200'}`}/>)}</div>

      {pakConfirmStep===1&&<div className="mt-6"><div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center"><FileText size={20}/></div><h3 className="text-xl font-bold mt-4">Pastikan PAK sudah lengkap</h3><p className="text-sm text-slate-600 mt-2 leading-relaxed">Apakah seluruh PAK yang menjadi riwayat usulan ini sudah digabung menjadi <b>1 file PDF</b>?</p><div className="mt-4"><Banner type="info">Jika Bapak/Ibu pernah menerima PAK sebelumnya, pastikan PAK tersebut tidak terlewat dari file gabungan.</Banner></div></div>}

      {pakConfirmStep===2&&<div className="mt-6"><div className="w-11 h-11 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center"><AlertTriangle size={20}/></div><h3 className="text-xl font-bold mt-4">Periksa urutan PAK</h3><p className="text-sm text-slate-600 mt-2 leading-relaxed">Pastikan halaman PAK di dalam PDF disusun dari <b>PAK paling lama ke PAK paling terbaru</b>, apa pun jenis PAK-nya.</p><div className="mt-4 bg-slate-50 border rounded-xl p-4 text-sm"><div className="font-bold">Contoh:</div><div className="mt-1 text-slate-600">PAK lama → PAK berikutnya → PAK terbaru</div><div className="mt-2 text-xs text-slate-500">Jenis PAK mengikuti riwayat masing-masing pegawai. Yang penting, jangan melewatkan PAK yang memang pernah diterbitkan dan digunakan dalam riwayat usulan.</div></div></div>}

      {pakConfirmStep===3&&<div className="mt-6"><div className="w-11 h-11 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><CheckCircle2 size={20}/></div><h3 className="text-xl font-bold mt-4">Konfirmasi PAK Gabungan</h3><p className="text-sm text-slate-600 mt-2">Sebelum diunggah, pastikan tiga hal berikut.</p><div className="mt-4 space-y-2 text-sm"><div className="border rounded-xl p-3 flex gap-2"><CheckCircle2 size={18} className="text-emerald-600 shrink-0"/><span>Semua PAK yang diperlukan sudah tergabung dalam satu PDF.</span></div><div className="border rounded-xl p-3 flex gap-2"><CheckCircle2 size={18} className="text-emerald-600 shrink-0"/><span>Urutan sudah dari PAK paling lama ke paling terbaru.</span></div><div className="border rounded-xl p-3 flex gap-2"><CheckCircle2 size={18} className="text-emerald-600 shrink-0"/><span>File yang dipilih adalah file PAK Gabungan yang benar.</span></div></div><div className="mt-4 text-xs text-slate-500 break-all"><b>File:</b> {newFile?.name} · {fmtSize(newFile?.size)}</div></div>}
    </div>
    <div className="border-t bg-slate-50 p-4 flex justify-between gap-2"><button type="button" onClick={()=>pakConfirmStep===1?setPakConfirmStep(0):setPakConfirmStep(pakConfirmStep-1)} className="px-4 py-2 rounded-xl bg-white border font-bold">{pakConfirmStep===1?'Periksa Lagi':'Kembali'}</button>{pakConfirmStep<3?<button type="button" onClick={()=>setPakConfirmStep(pakConfirmStep+1)} className="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold">{pakConfirmStep===1?'Ya, Sudah Lengkap':'Urutan Sudah Benar'}</button>:<button type="button" disabled={busy} onClick={performUpload} className="px-4 py-2 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold inline-flex items-center gap-2"><Upload size={15}/>{busy?'Mengunggah...':'Unggah PAK Gabungan'}</button>}</div>
  </div></div>:null;

  return <>
    <div className={`border rounded-xl p-4 ${doc?'border-emerald-200 bg-emerald-50/30':'border-slate-200'} ${isPakGabungan?'ring-1 ring-blue-100':''}`}>
      <div className="flex flex-col gap-4">
        <div className="flex-1"><div className="flex flex-wrap gap-2 items-center"><div className="font-bold">{req.name} {req.required&&<span className="text-rose-600">*</span>}</div>{!req.required&&<Pill tone="slate">Tidak wajib</Pill>}{doc&&<Pill tone="green">Sudah ada</Pill>}</div><div className="text-xs text-slate-500 mt-1 leading-relaxed">{req.hint}</div>{isPakGabungan&&<div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2">Gabungkan seluruh PAK yang menjadi riwayat usulan. Jika ada PAK sebelumnya, jangan dilewati. Susun halaman dari PAK paling lama ke paling terbaru.</div>}</div>

        {doc ? <div className="flex flex-wrap items-center gap-2 text-xs"><button type="button" onClick={()=>openPdf(`${API}/api/kp/my/document/view/${req.code}?period_id=${ctx.period.id}`)} className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 font-bold inline-flex items-center gap-1"><Eye size={14}/>Lihat</button><span className="text-slate-500">{doc.file_name} · {fmtSize(doc.size_bytes)}</span>{!disabled&&<button type="button" disabled={busy} onClick={remove} className="px-3 py-2 rounded-lg bg-slate-100 text-slate-700 font-bold">Ganti</button>}</div> : !disabled && <div className="grid lg:grid-cols-2 gap-3">
          <div className="border rounded-xl p-3 bg-slate-50">
            <div className="text-xs font-bold text-slate-700 mb-2">Pakai dokumen yang sudah ada</div>
            <select value={source} onChange={e=>setSource(e.target.value)} className="w-full border rounded-lg px-2 py-2 text-xs bg-white"><option value="">Pilih dokumen...</option>{candidates.map(c=><option key={`${c.source_kind}:${c.source_id}`} value={`${c.source_kind}:${c.source_id}`}>{c.source_verified?'Pernah dicek · ':''}{c.name} · {c.period||''}</option>)}</select>
            {selectedCandidate&&<div className="mt-2 flex flex-wrap gap-2"><button type="button" onClick={()=>openPdf(`${API}/api/kp/my/document/source/${selectedCandidate.source_kind}/${selectedCandidate.source_id}`)} className="px-3 py-2 rounded-lg bg-white border text-blue-700 font-bold text-xs inline-flex items-center gap-1"><Eye size={14}/>Lihat dulu</button><button type="button" disabled={busy} onClick={reuse} className="px-3 py-2 rounded-lg bg-slate-900 disabled:bg-slate-300 text-white font-bold text-xs inline-flex items-center gap-1"><Copy size={14}/>Pakai dokumen ini</button></div>}
            {!candidates.length&&<div className="text-xs text-slate-500 mt-2">Belum ada dokumen lama yang bisa dipilih.</div>}
          </div>

          <div className="border rounded-xl p-3">
            <div className="text-xs font-bold text-slate-700 mb-2">Upload PDF baru</div>
            <input type="file" accept="application/pdf,.pdf" onChange={e=>{setNewFile(e.target.files?.[0]||null);setPakConfirmStep(0);}} className="w-full border rounded-lg p-2 text-xs bg-white"/>
            <div className="mt-2 flex items-center justify-between gap-2"><span className="text-[11px] text-slate-500 truncate">{newFile?`${newFile.name} · ${fmtSize(newFile.size)}`:'Maksimal 900 KB'}</span><button type="button" disabled={!newFile||busy} onClick={upload} className="px-3 py-2 rounded-lg bg-blue-600 disabled:bg-slate-300 text-white font-bold text-xs inline-flex items-center gap-1"><Upload size={14}/>{busy?'Tunggu...':'Upload'}</button></div>
          </div>
        </div>}
      </div>
    </div>
    <PakConfirmModal/>
  </>;
}

async function downloadWithToken(url){const r=await kpFetch(url,{headers:authHeaders(false)});if(!r.ok){const d=await r.json().catch(()=>({}));return alert(d.msg||'Gagal download');}const blob=await r.blob();const cd=r.headers.get('content-disposition')||'';const m=cd.match(/filename="?([^";]+)"?/);const name=m?.[1]||'Usulan_KP.xlsx';const a=document.createElement('a');a.href=URL.createObjectURL(blob);a.download=name;a.click();URL.revokeObjectURL(a.href);}
